<template>
  <BaseLayout>
    <div class="page-single position-relative">
      <b-container
        fluid
      >
        <b-row class="justify-content-center">
          <b-col class="col-sm-8 col-md-7 col-lg-5 col-xl-3">
            <div class="text-center mb-6">
              <img
                src="/images/smartmatch-logo.png"
                class="h-6"
                alt=""
              >
            </div>

            <b-form
              class="card"
              @submit.prevent="signin"
            >
              <div
                v-if="isLoading"
                class="align-items-center d-flex justify-content-center card-body"
              >
                <div class="loader mx-auto" />
              </div>
              <div v-else>
                <div class="card-body p-6">
                  <div
                    class="card-title"
                    data-test="login-title"
                  >
                    Log into your account
                  </div>
                  <b-form-group
                    id="email-address-group-1"
                    label="Email address*"
                    label-for="email"
                    label-class="form-label"
                  >
                    <b-form-input
                      id="email-address-input"
                      v-model="email"
                      name="email"
                      type="email"
                      required
                      placeholder="Email address"
                      autocomplete="email"
                      data-test="email-input"
                      autofocus
                    />
                  </b-form-group>
                  <b-form-group
                    id="password-group-1"
                  >
                    <template
                      slot="label"
                    >
                      <label class="form-label">
                        Password*
                      </label>
                    </template>

                    <b-form-input
                      id="password"
                      v-model="password"
                      name="password"
                      type="password"
                      required
                      placeholder="Password"
                      autocomplete="current-password"
                      aria-autocomplete="list"
                      data-test="password-input"
                    />
                  </b-form-group>
                  <span class="mb-2">
                    <router-link
                      class="small"
                      :to="this.$route.query.redirect
                        ?`forgot-password?redirect=${this.$route.query.redirect}`
                        :`forgot-password`"
                    >
                      Forgotten your password?
                    </router-link>
                  </span>
                  <div class="form-footer login">
                    <b-button
                      id="submit"
                      variant="primary"
                      type="submit"
                      block
                      :disabled="isDisabled"
                      data-test="login-submit"
                    >
                      Log in
                    </b-button>
                  </div>
                  <div class="text-center text-muted mt-3">
                    Don't have account yet?
                    <router-link
                      :to="this.$route.query.redirect ?`register?redirect=${this.$route.query.redirect}`:`register`"
                      data-test="register-link"
                    >
                      Sign up
                    </router-link>
                  </div>
                </div>
              </div>
            </b-form>

            <div class="text-center text-muted mt-4">
              Need support? Contact our team <a
                href="mailto:support@smartology.net"
                target="_blank"
              >support@smartology.net</a>
            </div>
          </b-col>
        </b-row>
        <div
          class="header-brand d-flex justify-content-center mb-5 mr-0 smartology-logo-footer"
          style="z-index: 0;"
        >
          <a
            target="_blank"
            href="https://www.smartology.net/"
          >
            <img
              src="/images/logo.png"
              class="h-7 header-brand-img mr-0"
              alt="Smartology"
            >
          </a>
        </div>
      </b-container>
    </div>
  </BaseLayout>
</template>
<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import * as accountTypes from '@/modules/account/store/types';
import * as types from '@/modules/authentication/store/types';
import { noop } from 'lodash';

export default {
  name: 'Login',
  data: () => ({
    isLoading: false,
    email: '',
    password: '',
  }),
  computed: {
    ...mapState('authentication', {
      user: 'user',
    }),
    ...mapGetters('account', [
      accountTypes.GET_PROFILE_PROPERTIES,
    ]),
    isDisabled() {
      return !this.email || !this.password;
    },
    isUserInStore() {
      return (!!this.user.email && !!this.user.password);
    },
    isRbacDashboardEnabled() {
      const p = this[accountTypes.GET_PROFILE_PROPERTIES];
      return p?.isRbacDashboardEnabled === true || p?.isRbacDashboardEnabled === 'true';
    },
  },
  methods: {
    ...mapActions('authentication', [
      types.SIGN_IN,
      types.SIGN_OUT,
      types.CONFIRM_SIGN_UP,
    ]),
    ...mapActions('account', [
      accountTypes.UPDATE_ACCOUNT,
      accountTypes.FETCH_ACCOUNT,
    ]),
    async redirectAfterLogin(redirect, fallbackLocation) {
      if (redirect && redirect.startsWith('/accounts')) {
        window.location.href = `${process.env.VUE_APP_ACCOUNT_MANAGEMENT_URL}${redirect}`;
      } else if (redirect && redirect.startsWith('/reports')) {
        window.location.href = `${process.env.VUE_APP_REPORTING_URL}${redirect}`;
      } else {
        const destination = redirect || fallbackLocation;
        if (destination === '/dashboard') {
          await this.FETCH_ACCOUNT();
          if (this.isRbacDashboardEnabled) {
            window.location.href = `${process.env.VUE_APP_CAMPAIGN_MANAGEMENT_URL}/campaigns/`;
            return;
          }
        }
        this.$router.push(destination);
      }
    },
    async signin() {
      const { confirmationCode, redirect } = this.$route.query;
      this.isLoading = true;

      if (confirmationCode) {
        await this.signUp(confirmationCode);
      }
      this.SIGN_IN({
        email: this.email.toLowerCase(),
        password: this.password,
      })
        .then((location) => {
          if (location === '/confirm') {
            this.$router.push(redirect
              ? `${location}?redirect=${redirect}`
              : location);
          } else {
            this.redirectAfterLogin(redirect, location);
          }
        })
        .catch(() => { this.isLoading = false; });
    },
    async signUp(confirmationCode) {
      try {
        await this.CONFIRM_SIGN_UP({
          email: this.email.toLowerCase(),
          code: confirmationCode,
        });
      } catch (error) {
        noop();
      }
    },
  },
  async created() {
    if (this.isUserInStore) {
      this.isLoading = true;
      const location = await this.SIGN_IN({
        email: this.user.email.toLowerCase(),
        password: this.user.password,
      });
      this.redirectAfterLogin(this.$route.query.redirect, location || '/dashboard');
    }
  },
};
</script>
